import React, { useRef, useLayoutEffect, useCallback } from 'react'

const PLACEHOLDER_STUDENT = '{{student_name}}'
const PLACEHOLDER_PARENT = '{{parent_name}}'
const LABEL_STUDENT = 'Student Name'
const LABEL_PARENT = 'Parent Name'

const SPLIT_REGEX = /(\{\{student_name\}\}|\{\{parent_name\}\})/

function parseSegments(raw) {
	if (typeof raw !== 'string' || !raw) return []
	const parts = raw.split(SPLIT_REGEX)
	const segments = []
	for (const p of parts) {
		if (p === PLACEHOLDER_STUDENT)
			segments.push({
				type: 'placeholder',
				key: 'student_name',
				label: LABEL_STUDENT,
			})
		else if (p === PLACEHOLDER_PARENT)
			segments.push({
				type: 'placeholder',
				key: 'parent_name',
				label: LABEL_PARENT,
			})
		else if (p.length) segments.push({ type: 'text', value: p })
	}
	return segments
}

function escapeHtml(s) {
	const div = document.createElement('div')
	div.textContent = s
	return div.innerHTML
}

function segmentsToHtml(segments, chipClass) {
	return segments
		.map(seg => {
			if (seg.type === 'text') return escapeHtml(seg.value)
			return `<span class="${chipClass}" data-placeholder="${seg.key}" contenteditable="false">${escapeHtml(seg.label)}</span>`
		})
		.join('')
}

function serializeEditor(root) {
	if (!root) return ''
	let out = ''
	function walk(node) {
		if (node.nodeType === Node.TEXT_NODE) {
			out += node.textContent
			return
		}
		if (node.nodeType !== Node.ELEMENT_NODE) return
		const el = node
		const key = el.getAttribute?.('data-placeholder')
		if (key === 'student_name') {
			out += PLACEHOLDER_STUDENT
			return
		}
		if (key === 'parent_name') {
			out += PLACEHOLDER_PARENT
			return
		}
		for (let i = 0; i < el.childNodes.length; i++) walk(el.childNodes[i])
	}
	for (let i = 0; i < root.childNodes.length; i++) walk(root.childNodes[i])
	return out
}

function getCaretOffsetInRaw(root) {
	const sel = window.getSelection()
	if (!sel || sel.rangeCount === 0) return 0
	let anchorNode = sel.anchorNode
	let anchorOffset = sel.anchorOffset
	if (!root.contains(anchorNode)) return 0
	let offset = 0
	let found = false
	function walk(node) {
		if (found) return
		if (node.nodeType === Node.TEXT_NODE) {
			const len = node.textContent.length
			if (node === anchorNode) {
				offset += anchorOffset
				found = true
				return
			}
			offset += len
			return
		}
		if (node.nodeType !== Node.ELEMENT_NODE) return
		const el = node
		const key = el.getAttribute?.('data-placeholder')
		if (key === 'student_name' || key === 'parent_name') {
			if (el === anchorNode || el.contains(anchorNode)) {
				offset += key === 'student_name' ? 17 : 15
				found = true
				return
			}
			offset += key === 'student_name' ? 17 : 15
			return
		}
		for (let i = 0; i < el.childNodes.length; i++) {
			walk(el.childNodes[i])
			if (found) return
		}
	}
	walk(root)
	return offset
}

function setCaretToRawOffset(root, rawOffset) {
	const sel = window.getSelection()
	if (!sel) return
	let current = 0
	let targetNode = null
	let targetOffset = 0
	function walk(node) {
		if (targetNode !== null) return
		if (node.nodeType === Node.TEXT_NODE) {
			const len = node.textContent.length
			if (current + len >= rawOffset) {
				targetNode = node
				targetOffset = rawOffset - current
				return
			}
			current += len
			return
		}
		if (node.nodeType !== Node.ELEMENT_NODE) return
		const el = node
		const key = el.getAttribute?.('data-placeholder')
		if (key === 'student_name' || key === 'parent_name') {
			const len = key === 'student_name' ? 17 : 15
			if (current + len >= rawOffset) {
				targetNode = el
				targetOffset = 0
				return
			}
			current += len
			return
		}
		for (let i = 0; i < el.childNodes.length; i++) {
			walk(el.childNodes[i])
			if (targetNode !== null) return
		}
	}
	walk(root)
	if (targetNode) {
		const range = document.createRange()
		if (targetNode.nodeType === Node.TEXT_NODE) {
			range.setStart(
				targetNode,
				Math.min(targetOffset, targetNode.textContent.length),
			)
			range.collapse(true)
		} else {
			range.setStartAfter(targetNode)
			range.collapse(true)
		}
		sel.removeAllRanges()
		sel.addRange(range)
	} else {
		const range = document.createRange()
		range.selectNodeContents(root)
		range.collapse(false)
		sel.removeAllRanges()
		sel.addRange(range)
	}
}

const MessageWithPlaceholders = ({
	value,
	onChange,
	cursorOffsetRef,
	editorRef,
	className,
	placeholder,
	chipClassName,
}) => {
	const elRef = useRef(null)
	const setRef = useCallback(
		node => {
			elRef.current = node
			if (editorRef) editorRef.current = node
		},
		[editorRef],
	)

	const handleInput = useCallback(() => {
		if (!elRef.current) return
		const raw = serializeEditor(elRef.current)
		const offset = getCaretOffsetInRaw(elRef.current)
		if (cursorOffsetRef) cursorOffsetRef.current = offset
		onChange(raw)
	}, [onChange, cursorOffsetRef])

	useLayoutEffect(() => {
		if (!elRef.current) return
		const segments = parseSegments(value)
		const html = segmentsToHtml(segments, chipClassName)
		elRef.current.innerHTML = html
	}, [value, chipClassName])

	useLayoutEffect(() => {
		if (!elRef.current || cursorOffsetRef?.current == null) return
		setCaretToRawOffset(elRef.current, cursorOffsetRef.current)
		cursorOffsetRef.current = null
	}, [value, cursorOffsetRef])

	return (
		<div
			ref={setRef}
			className={className}
			contentEditable
			suppressContentEditableWarning
			onInput={handleInput}
			data-placeholder={placeholder}
			role="textbox"
			aria-multiline="true"
		/>
	)
}

export default MessageWithPlaceholders
export {
	parseSegments,
	setCaretToRawOffset,
	PLACEHOLDER_STUDENT,
	PLACEHOLDER_PARENT,
}
